import { authStore } from '@core/stores';
import { taskApi, userprofileApi } from '@core/api';
import type { Task } from '@core/types/task';
import type { Userprofile } from '@core/types/userprofile';
import { candidateFromProfile } from '@/features/userprofile/pickerCandidates';

export function referenceScope(): string {
  return [import.meta.env.VITE_PLATFORM_ID, import.meta.env.VITE_TENANT_ID, import.meta.env.VITE_PRODUCT_ID, authStore.userId, authStore.activeProfileId, authStore.isAuthenticated].join(':');
}
export type ProfileReference = { name: string; profileId?: string; profile?: Userprofile };
type Entry<T> = { value: T; expires: number };
const profiles = new Map<string, Entry<ProfileReference | null>>();
const pendingProfiles = new Map<string, Promise<ProfileReference | null>>();
const batch = new Map<string, { scope: string; id: string; resolve: (value: ProfileReference | null) => void }>();
let scheduled = false;
const keyFor = (scope: string, id: string) => `${scope}|${id}`;
export function cachedProfile(id: string, scope = referenceScope()): ProfileReference | null | undefined {
  const entry = profiles.get(keyFor(scope, id));
  return entry && entry.expires > Date.now() ? entry.value : undefined;
}
export function loadProfileReference(id: string, scope = referenceScope()): Promise<ProfileReference | null> {
  if (!id) return Promise.resolve(null);
  const cached = cachedProfile(id, scope);
  if (cached !== undefined) return Promise.resolve(cached);
  const key = keyFor(scope, id);
  const pending = pendingProfiles.get(key);
  if (pending) return pending;
  const promise = new Promise<ProfileReference | null>((resolve) => {
    batch.set(key, { scope, id, resolve });
  });
  pendingProfiles.set(key, promise);
  if (!scheduled) {
    scheduled = true;
    queueMicrotask(async () => {
      scheduled = false;
      const requests = [...batch.entries()];
      batch.clear();
      let visible = new Map<string, Userprofile>();
      const ids = [...new Set(requests.filter(([, request]) => request.scope === referenceScope()).map(([, request]) => request.id))];
      let batchFailed = false;
      try {
        if (ids.length) {
          const response = await userprofileApi.ListUserprofilesByIDs({ ids });
          visible = new Map((response.userprofiles?.profiles || []).map((profile) => [profile.entity?.id || '', profile]));
        }
      } catch { batchFailed = true; }
      await Promise.all(requests.map(async ([requestKey, request]) => {
        let result: ProfileReference | null = null;
        const profile = visible.get(request.id);
        if (!batchFailed && request.scope === referenceScope()) {
          if (profile) result = { name: candidateFromProfile(profile).displayName, profileId: profile.entity?.id, profile };
          else {
            try {
              // Audit records may contain a user id rather than a profile id.
              const response = await referenceRequest(() => userprofileApi.GetUserprofileDefault(request.id));
              const publicProfile = response.userprofile;
              if (publicProfile) {
                result = { name: candidateFromProfile(publicProfile).displayName };
                const profileId = publicProfile.entity?.id;
                if (profileId) {
                  try {
                    const readable = await referenceRequest(() => userprofileApi.GetUserprofile(profileId));
                    if (readable.userprofile) result = { name: candidateFromProfile(readable.userprofile).displayName, profileId, profile: readable.userprofile };
                  } catch { /* A public name does not grant access to a private profile card. */ }
                }
              }
            } catch { /* Never replace inaccessible identities with raw ids. */ }
          }
        }
        profiles.set(requestKey, { value: result, expires: Date.now() + (result ? 60_000 : 15_000) });
        pendingProfiles.delete(requestKey);
        request.resolve(result);
      }));
    });
  }
  return promise;
}

const tasks = new Map<string, Entry<Task | null>>();
const pendingTasks = new Map<string, Promise<Task | null>>();
const queue: Array<() => Promise<void>> = [];
let active = 0;
function drain() {
  while (active < 3 && queue.length) {
    active += 1;
    void queue.shift()!().finally(() => { active -= 1; drain(); });
  }
}
function referenceRequest<T>(request: () => Promise<T>): Promise<T> {
 return new Promise<T>((resolve,reject) => {
  queue.push(async () => { try { resolve(await request()); } catch (error) { reject(error); } });
  drain();
 });
}
export function cachedTask(id: string, scope = referenceScope()): Task | null | undefined {
  const entry = tasks.get(keyFor(scope, id));
  return entry && entry.expires > Date.now() ? entry.value : undefined;
}
export function loadTaskReference(id: string, scope = referenceScope()): Promise<Task | null> {
  const cached = cachedTask(id, scope);
  if (cached !== undefined) return Promise.resolve(cached);
  const key = keyFor(scope, id);
  const pending = pendingTasks.get(key);
  if (pending) return pending;
  const promise = new Promise<Task | null>((resolve) => {
    queue.push(async () => {
      let value: Task | null = null;
      try {
        if (scope === referenceScope()) value = (await taskApi.GetTask(id)).task || null;
      } catch { /* Deleted and inaccessible references expose no identifying data. */ }
      tasks.set(key, { value, expires: Date.now() + (value ? 60_000 : 15_000) });
      pendingTasks.delete(key);
      resolve(value);
    });
  });
  pendingTasks.set(key, promise);
  drain();
  return promise;
}
